import React, { useEffect, useRef, useState } from "react";
import { Row, Col, Stack, Button, Spinner, Container, Image } from "react-bootstrap";
import {
  fetchBeds,
  fetchEthnicities,
  fetchGenders,
  fetchJobs,
  fetchPrograms,
  fetchRaces,
  fetchRelationships,
  fetchRooms,
  patchBed,
  patchFieldValue,
  patchGuardian,
  patchMember,
  postFieldValue,
} from "../../services/httpClient";
import DatePicker from "../ui/DatePicker";
import Input from "../ui/Input";
import SelectObject from "../ui/SelectObject";
import InputPhone from "../ui/InputPhone";
import { useParams } from "react-router-dom";
import ToastMsg from "../ui/ToastMsg";
import FormCheck from "../ui/FormCheckbox";
import useApp from "../../utils/useApp";
import SelectObjectTypeahead from "../ui/SelectObjectTypeahead";
import ConfirmBan from "../ui/ConfirmBan";
import ConfirmDelete from "./ConfirmDelete";
import CustomField from "../ui/CustomField";

interface EditMemberState {
  user: any;
  userType: string;
  setUserToSave?: any;
  caseStatus: string;
  onClickDone: () => void;
  guardians: any[];
  household: any[];
  children: any[];
  getHousehold: () => void;
  getCase: () => void;
}

const EditMember = ({
  user,
  userType,
  setUserToSave,
  caseStatus,
  onClickDone,
  guardians,
  household,
  children,
  getHousehold,
  getCase,
}: EditMemberState) => {
  const { id } = useParams();
  const { auth }: any = useApp();
  const [firstName, setFirstName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const [preferredName, setPreferredName] = useState<string>("");
  const [isPrimary, setIsPrimary] = useState<boolean>(false);
  const [isPrimaryUpdated, setIsPrimaryUpdated] = useState<boolean>(false);
  const [email, setEmail] = useState<string>("");
  const [phone, setPhone] = useState<number | null>(null);
  const [relationship, setRelationship] = useState<string>("");
  const [intakeDate, setIntakeDate] = useState<string>();
  const [exitDate, setExitDate] = useState<string>();
  const [birthDate, setBirthDate] = useState<string>();
  const [school, setSchool] = useState<string>("");
  const [grade, setGrade] = useState<string>("");
  const [gender, setGender] = useState<string>("");
  const [race, setRace] = useState<string>("");
  const [ethnicity, setEthnicity] = useState<string>("");
  const [program, setProgram] = useState<any>("");
  const [level, setLevel] = useState<any>("");
  const [previousJob, setPreviousJob] = useState<string>("");
  const [job, setJob] = useState<any>();
  const [room, setRoom] = useState<any>();
  const [previousBed, setPreviousBed] = useState<any>();
  const [bed, setBed] = useState<any>();
  const [appAccess, setAppAccess] = useState<boolean>(false);
  const [custom, setCustom] = useState<any[]>([]);
  const [isBanned, setIsBanned] = useState<boolean>(false);
  const [profileImage, setProfileImage] = useState<string>("");
  const [saveIsEnabled, setSaveIsEnabled] = useState<boolean>(false);
  const [isValidEmail, setIsValidEmail] = useState<boolean>(true);
  const [isValidPhone, setIsValidPhone] = useState<boolean>(true);
  const [errMsg, setErrMsg] = useState("");
  const [relationships, setRelationships] = useState<any[]>([]);
  const [allRelationships, setAllRelationships] = useState<any[]>([]);
  const [genders, setGenders] = useState<any[]>([]);
  const [races, setRaces] = useState<any[]>([]);
  const [ethnicities, setEthnicities] = useState<any[]>([]);
  const [programs, setPrograms] = useState<any[]>([]);
  const [levels, setLevels] = useState<any[]>([]);
  const [jobs, setJobs] = useState<any[]>([]);
  const [rooms, setRooms] = useState<any[]>([]);
  const [beds, setBeds] = useState<any[]>([]);
  const [filteredBeds, setFilteredBeds] = useState<any[]>([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isBanning, setIsBanning] = useState(false);
  const [userToBan, setUserToBan] = useState<string>();
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [showToast, setShowToast] = useState<boolean>(false);
  const [toastBody, setToastBody] = useState<string>("");
  const [toastText, setToastText] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);
  const [progLoading, setProgLoading] = useState<boolean>(true);
  const [updated, setUpdated] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    getRelationships();
    getGenders();
    getRaces();
    getEthnicities();
    getPrograms();
    getJobs();
    if (auth?.profile.includeHMIS === 1) {
      getBeds();
    }

    setFirstName(user.firstname);
    setLastName(user.lastname);
    setPreferredName(user.preferredname);
    setIsPrimary(user?.is_primary === 1 ? true : false);
    setEmail(user?.email);
    setPhone(user.phonenumber === "" ? null : user.phonenumber);
    setRelationship(user?.relationship);
    setIntakeDate(user?.intake_at);
    setExitDate(user?.exit_at);
    setBirthDate(user?.birthdate);
    setGender(user?.gender);
    setRace(user?.race);
    setEthnicity(user?.ethnicity);
    setPreviousJob(user?.job);
    setIsBanned(user?.is_banned === 1 ? true : false);
    setAppAccess(user?.appaccess === 1 ? true : false);
    //if a child
    setSchool(user?.school);
    setGrade(user?.grade);

    if (user?.profile.length > 0) {
      user?.profile?.map((p: any) => {
        const base64String = "data:image/jpeg;base64, " + p?.data;
        setProfileImage(base64String);
      });
    } else {
      setProfileImage("");
    }

    //HMIS ONLY
    if (auth?.profile.includeHMIS === 1) {
      setRoom(user?.room);
      setPreviousBed(user?.bed);
      setBed(user?.bed);
    }

    setIsLoading(false);
  }, []);

  function validateEmail(email: string, access: boolean) {
    var regexp = /\S+@\S+\.\S+/;
    if (access) {
      return regexp.test(email);
    } else {
      return regexp.test(email) || email.length === 0;
    }
  }

  function validatePhone(phone: string, access: boolean) {
    let length = phone.length;
    if (access) {
      return length === 11;
    } else {
      return length === 11 || length === 1;
    }
  }

  const getRelationships = () => {
    fetchRelationships().then((res: any) => {
      setAllRelationships(res[0].data);
      setRelationships(
        res[0].data.filter((c: any) => {
          return c.key !== "child";
        }),
      );
    });
  };

  const getGenders = () => {
    fetchGenders().then((res: any) => {
      setGenders(res[0].data);
    });
  };

  const getRaces = () => {
    fetchRaces().then((res: any) => {
      setRaces(res[0].data);
    });
  };

  const getEthnicities = () => {
    fetchEthnicities().then((res: any) => {
      setEthnicities(res[0].data);
    });
  };

  const getPrograms = () => {
    setProgLoading(true);

    fetchPrograms()
      .then((res: any) => {
        let programs = res[0].data;
        programs = [{ id: 0, key: 0, name: "Select from List", programLevels: [] }, ...programs];
        setPrograms(programs);
        setLevels([]);

        const selectedProgram = programs.find((p: any) => p.key === user?.program);
        if (selectedProgram) {
          if (selectedProgram.programLevels && selectedProgram.programLevels.length > 0) {
            setLevels(selectedProgram.programLevels);
            if (user?.level_id) {
              const selectedLevel = selectedProgram.programLevels.find((lvl: any) => lvl.id === user.level_id);
              if (selectedLevel) {
                setLevel(selectedLevel);
              } else if (level !== null) {
                setLevel(null);
              }
            }
          }
          setProgram(selectedProgram);
        }
      })
      .finally(() => {
        setProgLoading(false);
      });
  };

  const getJobs = () => {
    fetchJobs().then((res: any) => {
      const jobs = res[0].data;
      var availJobs = jobs.filter((j: any) => j.positions_filled < j.positions_total);

      //Ensure user's job is included
      const userJob = jobs.find((j: any) => j.key === user?.job);

      if (userJob && !availJobs.some((j: any) => j.id === userJob.id)) {
        availJobs.push(userJob);
      }

      availJobs = [{ id: 0, key: 0, name: "Select from List" }, ...availJobs];
      setJobs(availJobs);

      const selectedJob = availJobs.find((j: any) => j.key === user?.job);
      if (selectedJob) {
        setJob(selectedJob);
      } else if (user?.job) {
        const currentJob = jobs.find((j: any) => j.key === user?.job);
        setJob(currentJob);
        setJobs((prevJobs) => [currentJob, ...prevJobs]);
      } else {
        setJob({ key: "", name: "" });
      }
    });
  };

  const getBeds = () => {
    fetchBeds()
      .then((res: any) => {
        var bedList = res[0].data;
        //include bed user is currently assigned to
        var currentBed;
        if (user?.bed) {
          currentBed = bedList.find((b: any) => b.key === user?.bed);
        }
        //filter to vacant beds
        bedList = bedList.filter((b: any) => b.occupant_id === null);

        if (currentBed) {
          //if user is assigned to a bed, add it to the list
          bedList.push(currentBed);
        }

        bedList.sort((a: any, b: any) => {
          // group by room name
          if (a.room_name !== b.room_name) {
            return a.room_name.localeCompare(b.room_name, undefined, { numeric: true, sensitivity: "base" });
          }
          // sort beds in each room alphanumerically
          return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" });
        });

        setBeds(bedList);
        if (user?.room) {
          const filtered = bedList.filter((b: any) => b.room_key === user?.room);
          setFilteredBeds(filtered);
        } else {
          setFilteredBeds(bedList);
        }
        getRooms();
      })
      .catch((err: any) => {
        console.log(err);
      })
      .finally(() => {
        setErrMsg("");
      });
  };

  const getRooms = () => {
    fetchRooms()
      .then((res: any) => {
        const roomList = res[0].data;
        roomList.sort((a: any, b: any) => {
          return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" });
        });
        setRooms(roomList);
      })
      .catch((err: any) => {
        console.log(err);
      });
  };

  const checkProgram = () => {
    //makes sure that if a program is selected, a level is also selected
    if (!program) return true;
    if (levels?.length > 0 && !level) {
      return false;
    }
    return true;
  };

  function enableSave() {
    if (firstName && lastName && checkProgram()) {
      if (appAccess && isValidEmail && isValidPhone) {
        setSaveIsEnabled(true);
        setUserToSave(true);
      } else if (!appAccess) {
        setSaveIsEnabled(true);
        setUserToSave(true);
      }
    } else {
      setSaveIsEnabled(false);
      setUserToSave(false);
    }
  }

  const updateFirstName = (e: any) => {
    setFirstName(e.target.value);

    if (e.target.value && lastName && checkProgram()) {
      if (appAccess && isValidEmail && isValidPhone) {
        setSaveIsEnabled(true);
        setUserToSave(true);
      } else if (!appAccess) {
        setSaveIsEnabled(true);
        setUserToSave(true);
      }
    } else {
      setSaveIsEnabled(false);
      setUserToSave(false);
    }
  };

  const updateLastName = (e: any) => {
    setLastName(e.target.value);

    if (e.target.value && firstName && checkProgram()) {
      if (appAccess && isValidEmail && isValidPhone) {
        setSaveIsEnabled(true);
        setUserToSave(true);
      } else if (!appAccess) {
        setSaveIsEnabled(true);
        setUserToSave(true);
      }
    } else {
      setSaveIsEnabled(false);
      setUserToSave(false);
    }
  };

  const updatePreferredName = (e: any) => {
    setPreferredName(e.target.value);
    enableSave();
  };

  const updateEmail = (e: any) => {
    setEmail(e.target.value);
    let validEmail = validateEmail(e.target.value, appAccess);

    if (validEmail) {
      setIsValidEmail(true);

      if (appAccess) {
        if (isValidPhone) {
          if (phone && firstName && lastName && checkProgram()) {
            setSaveIsEnabled(true);
            setUserToSave(true);
          } else {
            setSaveIsEnabled(false);
            setUserToSave(false);
          }
        }
      } else {
        if (firstName && lastName) {
          if (validEmail && isValidPhone && checkProgram()) {
            setSaveIsEnabled(true);
            setUserToSave(true);
          } else {
            setSaveIsEnabled(false);
            setUserToSave(false);
          }
        }
      }
    } else {
      setIsValidEmail(false);
      setSaveIsEnabled(false);
      setUserToSave(false);
    }
  };

  const updatePhone = (e: any) => {
    setPhone(e);
    let validPhone = validatePhone(e, appAccess);

    if (validPhone) {
      setIsValidPhone(true);

      if (userType === "Guardian") {
        if (appAccess) {
          if (isValidEmail) {
            if (email && firstName && lastName && checkProgram()) {
              setSaveIsEnabled(true);
              setUserToSave(true);
            } else {
              setSaveIsEnabled(false);
              setUserToSave(false);
            }
          }
        } else {
          if (firstName && lastName) {
            if (isValidEmail && validPhone && checkProgram()) {
              setSaveIsEnabled(true);
              setUserToSave(true);
            } else {
              setSaveIsEnabled(false);
              setUserToSave(false);
            }
          }
        }
      } else {
        setSaveIsEnabled(true);
        setUserToSave(true);
      }
    } else {
      setIsValidPhone(false);
      setSaveIsEnabled(false);
      setUserToSave(false);
    }
  };

  const updateAppAccess = () => {
    if (!appAccess) {
      const validEmail = validateEmail(email, true);
      const validPhone = validatePhone(phone ? phone.toString() : "", true);
      setIsValidEmail(validEmail);
      setIsValidPhone(validPhone);

      if (firstName && lastName && email && phone && checkProgram()) {
        if (validEmail && validPhone) {
          setSaveIsEnabled(true);
          setUserToSave(true);
        } else {
          setSaveIsEnabled(false);
          setUserToSave(false);
        }
      } else {
        setSaveIsEnabled(false);
        setUserToSave(false);
      }
    } else {
      if (firstName && lastName) {
        const validEmail = validateEmail(email, false);
        const validPhone = validatePhone(phone ? phone.toString() : "", false);
        setIsValidEmail(validEmail);
        setIsValidPhone(validPhone);

        if (validEmail && validPhone) {
          setSaveIsEnabled(true);
          setUserToSave(true);
        } else {
          setSaveIsEnabled(false);
          setUserToSave(false);
        }
      } else {
        setSaveIsEnabled(false);
        setUserToSave(false);
      }
    }

    setAppAccess(!appAccess);
  };

  const updateRelationship = (e: any) => {
    setRelationship(e.target.value);
    enableSave();
  };

  const updateIntakeDate = (e: any) => {
    setIntakeDate(e);
    enableSave();
  };

  const updateBirthDate = (e: any) => {
    if (e) {
      setBirthDate(e);
    } else {
      setBirthDate("");
    }
    enableSave();
  };

  const updateGender = (e: any) => {
    setGender(e.target.value);
    enableSave();
  };

  const updateRace = (e: any) => {
    setRace(e.target.value);
    enableSave();
  };

  const updateEthnicity = (e: any) => {
    setEthnicity(e.target.value);
    enableSave();
  };

  const updateSchool = (e: any) => {
    setSchool(e.target.value);
    enableSave();
  };

  const updateGrade = (e: any) => {
    setGrade(e.target.value);
    enableSave();
  };

  const updateProgram = (e: any) => {
    setUpdated(true);
    setLevel(null);
    setLevels([]);
    if (e.programLevels && e.programLevels.length > 0) {
      setLevels(e.programLevels);
    }
    setProgram(e);
  };

  const updateLevel = (e: any) => {
    setUpdated(true);
    setLevel(e);
  };

  useEffect(() => {
    if (progLoading) return;
    if (!updated) return;
    enableSave();
  }, [program, level, progLoading]);

  const updateJob = (e: any) => {
    setJob(e);
    enableSave();
  };

  const updateRoom = (e: any) => {
    setRoom(e.target.value);
    setBed(null);
    const filtered = beds.filter((b: any) => b.room_key === e.target.value);
    setFilteredBeds(filtered);
  };

  const updateBed = (e: any) => {
    setBed(e.target.value);
    enableSave();
  };

  const updateIsPrimary = () => {
    if (!isPrimary) {
      setIsPrimaryUpdated(true);
    }
    setIsPrimary(!isPrimary);
    enableSave();
  };

  const onClickSave = () => {
    setErrMsg("");

    if (userType === "Guardian") {
      //update / create custom field values
      custom.map((cust: any) => {
        if (cust?.fieldValueId) {
          //patch
          const fieldVal = {
            value: cust?.value,
          };

          patchFieldValue(fieldVal, cust?.fieldValueId).catch((err: any) => {
            console.log(err);
            setErrMsg(err?.response?.data?.msg);
          });
        } else {
          //post
          const fieldVal = {
            field_id: cust?.fieldId,
            person_id: user?.id,
            person_type: "guardian",
            value: cust?.value,
          };

          postFieldValue(fieldVal).catch((err: any) => {
            console.log(err);
            setErrMsg(err?.response?.data?.msg);
          });
        }
      });

      if (isPrimaryUpdated) {
        const otherGuardians = guardians.filter((g: any) => {
          return g.id !== user?.id;
        });

        const otherUpdatedGuardian = {
          is_primary: false,
        };

        otherGuardians.map((o: any) => {
          patchGuardian(otherUpdatedGuardian, id, o?.id)
            .then((res: any) => {})
            .catch((err: any) => {
              setErrMsg(err?.response?.data?.msg);
            });
        });
      }

      var updatedUser: any = {
        firstname: firstName,
        lastname: lastName,
        preferredname: preferredName,
        is_primary: isPrimary,
        email: email,
        phonenumber: phone === 1 ? null : phone,
        relationship: relationship,
        intake_at: intakeDate,
        exit_at: exitDate,
        birthdate: birthDate,
        gender: gender,
        race: race,
        ethnicity: ethnicity,
        program: program?.key === 0 ? 0 : program?.key ? program.key : null,
        level_id: level?.id === 0 ? 0 : level?.id ? level.id : null,
        previous_job: previousJob,
        job: job?.key === 0 ? 0 : job?.key ? job.key : null,
      };

      if (updatedUser.program === 0) {
        updatedUser.level_id = null;
      }

      const prevAppAccess = user?.appaccess === 1 ? true : false;
      if (prevAppAccess !== appAccess) {
        updatedUser.appaccess = appAccess;
      }

      //update user
      patchGuardian(updatedUser, id, user?.id)
        .then((res: any) => {
          setSaveIsEnabled(false);
          setShowToast(true);
          setToastText("User Updated");
          const toast = preferredName ? firstName + " '" + preferredName + "' " + lastName : firstName + " " + lastName;
          setToastBody(toast);

          if (auth?.profile.includeHMIS === 1) {
            if (bed != user?.bed) {
              //setting bed vacant
              if (bed == 0) {
                const updatedBed = {
                  occupant_id: bed,
                };

                patchBed(updatedBed, previousBed)
                  .then((res: any) => {})
                  .catch((err: any) => {
                    setErrMsg(err?.response?.data?.msg);
                  });
              } else if (bed) {
                const updatedBed = {
                  occupant_id: user?.id,
                  occupant_type: "guardian",
                };

                //update bed assignment
                patchBed(updatedBed, bed)
                  .then((res: any) => {})
                  .catch((err: any) => {
                    setErrMsg(err?.response?.data?.msg);
                  });
              }
            }
          }
        })
        .catch((err: any) => {
          setErrMsg(err?.response?.data?.msg);
        })
        .finally(() => {
          setUserToSave(false);
        });
    } else {
      //update / create custom field values
      custom.map((cust: any) => {
        if (cust?.fieldValueId) {
          //patch
          const fieldVal = {
            value: cust?.value,
          };

          patchFieldValue(fieldVal, cust?.fieldValueId).catch((err: any) => {
            console.log(err);
            setErrMsg(err?.response?.data?.msg);
          });
        } else {
          //post
          const fieldVal = {
            field_id: cust?.fieldId,
            person_id: user?.id,
            person_type: "member",
            value: cust?.value,
          };

          postFieldValue(fieldVal).catch((err: any) => {
            console.log(err);
            setErrMsg(err?.response?.data?.msg);
          });
        }
      });

      const updatedMember = {
        firstname: firstName,
        lastname: lastName,
        preferredname: preferredName,
        phonenumber: phone,
        relationship: relationship,
        intake_at: intakeDate,
        exit_at: exitDate,
        birthdate: birthDate,
        gender: gender,
        race: race,
        ethnicity: ethnicity,
        program: program?.key === 0 ? 0 : program?.key ? program.key : null,
        level_id: level?.id === 0 ? 0 : level?.id ? level.id : null,
        previous_job: previousJob,
        job: job?.key === 0 ? 0 : job?.key ? job.key : null,
        school: school,
        grade: grade,
      };

      if (updatedMember.program === 0) {
        updatedMember.level_id = null;
      }

      //update member
      patchMember(updatedMember, id, user?.id)
        .then((res: any) => {
          setSaveIsEnabled(false);
          setShowToast(true);
          setToastText("Member Updated");
          const toast = preferredName ? firstName + " '" + preferredName + "' " + lastName : firstName + " " + lastName;
          setToastBody(toast);

          if (auth?.profile.includeHMIS === 1) {
            if (bed != user?.bed) {
              //setting bed vacant
              if (bed == 0) {
                const updatedBed = {
                  occupant_id: bed,
                };

                patchBed(updatedBed, previousBed)
                  .then((res: any) => {})
                  .catch((err: any) => {
                    setErrMsg(err?.response?.data?.msg);
                  });
              } else if (bed) {
                const updatedBed = {
                  occupant_id: user?.id,
                  occupant_type: "member",
                };

                //update bed assignment
                patchBed(updatedBed, bed)
                  .then((res: any) => {})
                  .catch((err: any) => {
                    setErrMsg(err?.response?.data?.msg);
                  });
              }
            }
          }
        })
        .catch((err: any) => {
          setErrMsg(err?.response?.data?.msg);
        })
        .finally(() => {
          setUserToSave(false);
        });
    }
  };

  const onClickSelectProfilePic = (event: any) => {
    fileInputRef?.current?.click();
  };

  const handleFileChange = (event: any) => {
    setErrMsg("");
    setIsImageLoading(true);

    const file = event.target.files[0];
    if (file) {
      // Check if the file is an image
      if (file.type.startsWith("image/")) {
        // The file is an image; proceed with upload
        var reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = function () {
          if (reader.result) {
            uploadFileData(reader.result.toString().split(",")[1]);
          }
        };
        reader.onerror = function (error) {
          setIsImageLoading(false);
        };
      } else {
        // Not an image; display an error or ignore the file
        setErrMsg("Please select a valid image file");
        setIsImageLoading(false);
      }
    } else {
      setIsImageLoading(false);
    }
  };

  async function uploadFileData(data: any) {
    setProfileImage(data);

    var image;
    if (user?.profile_id) {
      image = {
        profile_id: user?.profile_id,
        profile_data: data,
      };
    } else {
      image = {
        profile_data: data,
      };
    }

    if (userType === "Guardian") {
      patchGuardian(image, id, user?.id)
        .then((res: any) => {
          const base64String = "data:image/jpeg;base64, " + data;
          setProfileImage(base64String);

          setShowToast(true);
          const toast = preferredName ? firstName + " '" + preferredName + "' " + lastName : firstName + " " + lastName;
          setToastText("Guardian Profile Image Updated");
          setToastBody(toast);
          setIsImageLoading(false);
        })
        .catch((err: any) => {
          setErrMsg(err?.response?.data?.msg);
          setIsImageLoading(false);
        });
    } else {
      patchMember(image, id, user?.id)
        .then((res: any) => {
          const base64String = "data:image/jpeg;base64, " + data;
          setProfileImage(base64String);

          setShowToast(true);
          const toast = preferredName ? firstName + " '" + preferredName + "' " + lastName : firstName + " " + lastName;
          setToastText("Member Profile Image Updated");
          setToastBody(toast);
          setIsImageLoading(false);
        })
        .catch((err: any) => {
          setErrMsg(err?.response?.data?.msg);
          setIsImageLoading(false);
        });
    }
  }

  const onClickBan = () => {
    setIsBanning(true);
    setUserToBan(firstName + " " + lastName);
  };

  const onClickDelete = () => {
    setIsDeleting(true);
  };

  const onClickDeleteProfilePic = () => {
    setErrMsg("");
    const image = {
      profile_id: user?.profile_id,
      profile_data: null,
    };

    setIsLoading(true);

    if (userType === "Guardian") {
      patchGuardian(image, id, user?.id)
        .then((res: any) => {
          setProfileImage("");

          setShowToast(true);
          const toast = preferredName ? firstName + " '" + preferredName + "' " + lastName : firstName + " " + lastName;
          setToastText("Guardian Profile Image Removed");
          setToastBody(toast);
        })
        .catch((err: any) => {
          setErrMsg(err?.response?.data?.msg);
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      patchMember(image, id, user?.id)
        .then((res: any) => {
          setProfileImage("");

          setShowToast(true);
          const toast = preferredName ? firstName + " '" + preferredName + "' " + lastName : firstName + " " + lastName;
          setToastText("Member Profile Image Removed");
          setToastBody(toast);
        })
        .catch((err: any) => {
          setErrMsg(err?.response?.data?.msg);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  };

  return (
    <>
      <ToastMsg showToast={showToast} setShowToast={setShowToast} toastText={toastText} toastBody={toastBody} />
      {isDeleting ? (
        <ConfirmDelete
          setIsDeleting={setIsDeleting}
          user={user}
          family={[...guardians, ...children, ...household]}
          fetchHousehold={getHousehold}
          caseStatus={caseStatus}
          getCase={getCase}
          onDone={onClickDone}
          setShowToast={setShowToast}
          setToastText={setToastText}
          setToastBody={setToastBody}
        />
      ) : isBanning ? (
        <ConfirmBan
          user={user}
          previousJob={previousJob}
          isBanned={isBanned}
          setIsBanning={setIsBanning}
          userToBan={userToBan}
          setUserToBan={setUserToBan}
          fetchHousehold={() => setIsBanned(!isBanned)}
          resetCurrentView={() => {}}
          type={userType}
          caseStatus={caseStatus}
          setShowToast={setShowToast}
          setToastText={setToastText}
          setToastBody={setToastBody}
        />
      ) : isLoading ? (
        <Container>
          <Button size="sm" className="spinner-button spinner-button-main">
            <Spinner animation="border" style={{ color: "#F6893D" }} />
          </Button>
        </Container>
      ) : (
        <>
          <Row className="mb-3">
            <Col>
              <h4 className="app-header-text">{"Edit Household Member"}</h4>
            </Col>
            <Col className="text-end">
              <Stack className="stack-float" direction="horizontal" gap={3}>
                <Button className="app-danger-color button" onClick={() => onClickDelete()} disabled={isDeleting}>
                  Delete
                </Button>
                <div className="vr" />
                <Button className="app-secondary-color button" onClick={() => onClickSave()} disabled={!saveIsEnabled}>
                  Save
                </Button>
                <div className="vr" />
                <Button className="app-primary-color button" onClick={() => onClickDone()}>
                  Cancel
                </Button>
              </Stack>
            </Col>
          </Row>
          <p className={errMsg ? "errmsg" : "offscreen"} aria-live="assertive">
            {errMsg}
          </p>
          <Stack gap={3} className="mb-3">
            <Row className="h-auto">
              <Col xs={3}>
                <Input label="First Name" type="text" value={firstName} onUpdate={(e: any) => updateFirstName(e)} required={true} />
              </Col>
              <Col xs={3}>
                <Input label="Last Name" type="text" value={lastName} onUpdate={(e: any) => updateLastName(e)} required={true} />
              </Col>
              <Col xs={3}>
                <Input label="Preferred Name" type="text" value={preferredName} onUpdate={(e: any) => updatePreferredName(e)} />
              </Col>
            </Row>
            <Row className="h-auto">
              <Col xs={6}>
                <Stack gap={3}>
                  <Row>
                    {userType === "Guardian" && (
                      <Col xs={6}>
                        <Input label="Email" type="text" value={email} onUpdate={(e: any) => updateEmail(e)} required={appAccess} />
                      </Col>
                    )}
                    <Col xs={6}>
                      <InputPhone
                        label="Phone"
                        value={phone}
                        onUpdate={(e: any) => updatePhone(e)}
                        required={userType === "Guardian" ? appAccess : false}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col xs={6}>
                      <DatePicker
                        label="Intake Date"
                        currentValue={intakeDate}
                        setCurrentValue={updateIntakeDate}
                        hasMaxDate={true}
                        disabled={caseStatus === "active" ? false : true}
                      />
                    </Col>
                    {user?.exit_at && (
                      <Col xs={6}>
                        <DatePicker
                          label="Exit Date"
                          currentValue={exitDate}
                          setCurrentValue={setExitDate}
                          hasMaxDate={true}
                          disabled={true}
                        />
                      </Col>
                    )}
                    <Col className={user?.exit_at ? "col-6 mt-3" : "col-6"}>
                      <DatePicker label="Birth Date" currentValue={birthDate} setCurrentValue={updateBirthDate} />
                    </Col>
                  </Row>
                  <Row>
                    <Col xs={6}>
                      <SelectObject
                        label="Relationship"
                        value={relationship}
                        options={userType !== "Guardian" ? allRelationships : relationships}
                        onUpdate={(e: any) => updateRelationship(e)}
                      />
                    </Col>
                    <Col xs={6}>
                      <SelectObject label="Gender" value={gender} options={genders} onUpdate={(e: any) => updateGender(e)} />
                    </Col>
                  </Row>
                  <Row>
                    <Col xs={6}>
                      <SelectObject label="Ethnicity" value={ethnicity} options={ethnicities} onUpdate={(e: any) => updateEthnicity(e)} />
                    </Col>
                    <Col xs={6}>
                      <SelectObject label="Race" value={race} options={races} onUpdate={(e: any) => updateRace(e)} />
                    </Col>
                  </Row>
                </Stack>
              </Col>
              <Col xs={3}>
                {isImageLoading ? (
                  <Button size="sm" className="spinner-button spinner-button-main">
                    <Spinner animation="border" style={{ color: "#F6893D" }} />
                  </Button>
                ) : (
                  <Stack gap={3}>
                    {profileImage ? (
                      <Image src={profileImage} rounded className="image-profile" />
                    ) : (
                      <Image src="/blank-profile-picture.jpg" className="image-profile" />
                    )}
                    <Button className="app-primary-color-inverse button" onClick={(e: any) => onClickSelectProfilePic(e)}>
                      {profileImage ? "Change Image" : "Upload Image"}
                    </Button>
                    {profileImage && (
                      <Button className="app-danger-color button" onClick={() => onClickDeleteProfilePic()}>
                        Remove Image
                      </Button>
                    )}
                  </Stack>
                )}
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  accept="image/*"
                  onChange={handleFileChange} // Handle the file change
                />
              </Col>
            </Row>
            {user?.relationship === "child" && (
              <Row className="h-auto">
                <Col xs={3}>
                  <Input label="School" type="text" value={school} onUpdate={(e: any) => updateSchool(e)} />
                </Col>
                <Col xs={3}>
                  <Input label="Grade" type="text" value={grade} onUpdate={(e: any) => updateGrade(e)} />
                </Col>
              </Row>
            )}
            {(programs.length > 0 || jobs.length > 0) && (
              <Row className="h-auto">
                {programs.length > 0 && (
                  <Col xs={3}>
                    <SelectObjectTypeahead label="Program" value={program} options={programs} onUpdate={(e: any) => updateProgram(e)} />
                  </Col>
                )}
                {levels.length > 0 && (
                  <Col xs={3}>
                    <SelectObjectTypeahead label="Program Level" value={level} options={levels} onUpdate={(e: any) => updateLevel(e)} />
                  </Col>
                )}
                {jobs.length > 0 && (
                  <Col xs={3}>
                    <SelectObjectTypeahead label="Job" value={job} options={jobs} onUpdate={(e: any) => updateJob(e)} />
                  </Col>
                )}
              </Row>
            )}

            {rooms.length > 0 && beds.length > 0 && (
              <Row className="h-auto">
                <Col xs={3}>
                  <SelectObject label="Room" value={room} options={rooms} onUpdate={(e: any) => updateRoom(e)} />
                </Col>
                <Col xs={3}>
                  <SelectObject
                    label="Bed"
                    value={bed}
                    options={filteredBeds}
                    onUpdate={(e: any) => updateBed(e)}
                    disabled={room ? false : true}
                    initialOption="Vacant"
                  />
                </Col>
              </Row>
            )}
          </Stack>
          {user?.customfields?.length > 0 && (
            <Row className="h-auto">
              {user?.customfields?.map((custom: any, index: number) => (
                <CustomField
                  key={index}
                  fieldValueId={custom?.field_value_id}
                  fieldId={custom?.field_id}
                  label={custom?.label}
                  type={custom?.type}
                  val={custom?.value}
                  setCustom={setCustom}
                  enableSave={enableSave}
                  columns={3}
                />
              ))}
            </Row>
          )}
          <Row className="h-auto mb-3">
            <Col xs={6} className="text-start">
              <Stack direction="horizontal" gap={3}>
                <Button className="app-tertiary-color-inverse button" onClick={() => onClickBan()}>
                  {isBanned ? "Unban" : "Ban"}
                </Button>
                <small className="text-muted">{user?.number_infractions + " Infraction(s)"}</small>
              </Stack>
            </Col>
            {userType === "Guardian" && (
              <Col xs={6}>
                <FormCheck label="Main Contact" value={isPrimary} onUpdate={(e: any) => updateIsPrimary()} />
                <FormCheck label="Mobile App Access" value={appAccess} onUpdate={(e: any) => updateAppAccess()} css={"mb-0"} />
                <small>{"(Requires email and phone)"}</small>
              </Col>
            )}
          </Row>
        </>
      )}
    </>
  );
};

export default EditMember;
